import Image from 'next/legacy/image';
import { useState } from 'react';

import * as S from './ProductInfoBox.sc';

import getImagePath from '@utils/getImagePath';
import { imageSquareLoader } from '@utils/imageLoaders';

import Description from '@components/Description/Description';
import Modal from '@components/Modal/Modal';
import ProductInfoMeta, {
  ProductMetaProps,
} from './ProductInfoMeta/ProductInfoMeta';
import ProductInfoPurchase, {
  ProductInfoPurchaseProps,
} from './ProductInfoPurchase/ProductInfoPurchase';
import SecondaryTitle from '@components/SecondaryTitle/SecondaryTitle';

import { event } from '@utils/gtag';

interface ProductInfoBoxProps
  extends ProductMetaProps,
    ProductInfoPurchaseProps {
  align: string;
  description: string;
  image: Image;
  position?: string;
  section: Section;
  showPurchase: boolean;
  title: string;
}

const ProductInfoBox = ({
  align,
  brand,
  description,
  figures,
  figuresName,
  image,
  parts,
  partsName,
  position,
  reference,
  referenceName,
  releaseStatus,
  section,
  showPurchase,
  stores,
  title,
}: ProductInfoBoxProps) => {
  const [showModal, setShowModal] = useState(false);
  const blurImage = getImagePath({
    dimensions: '6x6',
    fitBackground: 'ffffff',
    quality: 20,
    url: image.filename,
  });
  const imageUrl = image
    ? getImagePath({
        url: image.filename,
        dimensions: '400x400',
        quality: '50',
        fitBackground: 'ffffff',
      })
    : null;
  const zoomedImageUrl = image
    ? getImagePath({ url: image.filename, fitBackground: 'ffffff' })
    : null;
  const styleSection = section.toLowerCase();

  return (
    <S.Section>
      <S.Grid data-align={align}>
        <S.Container>
          <SecondaryTitle>
            {position ? `${position}. ` : null}
            {title}
          </SecondaryTitle>
          <ProductInfoMeta
            figures={figures}
            figuresName={figuresName}
            parts={parts}
            partsName={partsName}
            reference={reference}
            referenceName={referenceName}
            releaseStatus={releaseStatus}
            section={section}
          />
          <Description content={description}></Description>
          {showPurchase ? (
            <ProductInfoPurchase
              brand={brand}
              releaseStatus={releaseStatus}
              reference={reference}
              section={section}
              stores={stores}
              title={title}
            ></ProductInfoPurchase>
          ) : null}
        </S.Container>
        <S.ImageContainer>
          <S.ImageRotate data-align={align}>
            {imageUrl ? (
              <>
                <S.ProductImage data-align={align}>
                  <S.Button
                    onClick={() => {
                      event({
                        category: section,
                        action: 'expand_image',
                        label: title,
                      });

                      setShowModal(true);
                    }}
                  >
                    <Image
                      alt={image.alt || title}
                      blurDataURL={blurImage}
                      height={400}
                      placeholder="blur"
                      src={imageUrl}
                      unoptimized={true}
                      width={400}
                    />
                  </S.Button>
                </S.ProductImage>
                <Modal
                  fullScreen={true}
                  onClose={() => {
                    event({
                      category: section,
                      action: 'close_expanded_image',
                      label: title,
                    });

                    setShowModal(false);
                  }}
                  section={section}
                  show={showModal}
                  title={title}
                >
                  <S.ProductImageFull>
                    <Image
                      alt={image.alt || title}
                      blurDataURL={blurImage}
                      layout="fill"
                      loader={imageSquareLoader}
                      objectFit="contain"
                      placeholder="blur"
                      src={zoomedImageUrl || imageUrl}
                    />
                  </S.ProductImageFull>
                </Modal>
              </>
            ) : null}
          </S.ImageRotate>
        </S.ImageContainer>
      </S.Grid>
      <S.Background>
        <S.BackgroundImage data-align={align} />
      </S.Background>
    </S.Section>
  );
};

export default ProductInfoBox;
